import React, { Suspense, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PaymentBrick, { type PaymentBrickProps } from './PaymentBrick';
import type { PaymentProviderRoutesDto } from '../../../types/checkoutPayment';
import { applyCpfMask } from '../../../utils/fieldMasks';

const PagarMePaymentBrick = React.lazy(() => import('./PagarMePaymentBrick'));

export type CheckoutTab = 'pix' | 'credit_card';

export interface CheckoutPaymentRouterProps {
  amount: number;
  payerEmail?: string;
  preferenceId?: string;
  routes: PaymentProviderRoutesDto;
  checkoutTab: CheckoutTab;
  onCheckoutTabChange: (tab: CheckoutTab) => void;
  onCardTokenReceived: NonNullable<PaymentBrickProps['onCardTokenReceived']>;
  onError?: (error: unknown) => void;
  allowInstallments?: boolean;
  maxInstallments?: number;
  customerName?: string;
  customerCPF?: string;
  /** Geração de Pix no Pagar.me (servidor cria cobrança e devolve QR / copia-e-cola). */
  onRequestPagarmePix?: (cpfDigits: string) => Promise<void>;
  pagarmePixBusy?: boolean;
}

/**
 * Orquestrador: monta só o fluxo do método ativo (lazy para Pagar.me cartão) e evita dois SDKs pesados ao mesmo tempo.
 */
const CheckoutPaymentRouter: React.FC<CheckoutPaymentRouterProps> = ({
  amount,
  payerEmail,
  preferenceId,
  routes,
  checkoutTab,
  onCheckoutTabChange,
  onCardTokenReceived,
  onError,
  allowInstallments = false,
  maxInstallments = 12,
  customerName = '',
  customerCPF = '',
  onRequestPagarmePix,
  pagarmePixBusy = false,
}) => {
  const pixProvider = routes.pix;
  const cardProvider = routes.credit_card;

  const showPixTab = pixProvider === 'mercadopago' || pixProvider === 'pagarme';
  const showCardTab = cardProvider === 'mercadopago' || cardProvider === 'pagarme';

  const [pixCpf, setPixCpf] = useState(() =>
    customerCPF && customerCPF.length >= 11 ? applyCpfMask(customerCPF.replace(/\D/g, '')) : ''
  );

  useEffect(() => {
    if (customerCPF && customerCPF.replace(/\D/g, '').length >= 11) {
      setPixCpf(applyCpfMask(customerCPF.replace(/\D/g, '')));
    }
  }, [customerCPF]);

  useEffect(() => {
    if (!showPixTab && showCardTab && checkoutTab !== 'credit_card') {
      onCheckoutTabChange('credit_card');
    }
    if (showPixTab && !showCardTab && checkoutTab !== 'pix') {
      onCheckoutTabChange('pix');
    }
  }, [showPixTab, showCardTab, checkoutTab, onCheckoutTabChange]);

  if (!showPixTab && !showCardTab) {
    return (
      <Typography color="error">
        Nenhuma forma de pagamento disponível. Verifique PAYMENT_ROUTE_JSON / rotas no servidor.
      </Typography>
    );
  }

  const pixCpfDigits = pixCpf.replace(/\D/g, '');

  return (
    <Box>
      {showPixTab && showCardTab ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Forma de pagamento
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={checkoutTab}
            onChange={(_e, v: CheckoutTab | null) => v && onCheckoutTabChange(v)}
          >
            <ToggleButton value="pix">Pix</ToggleButton>
            <ToggleButton value="credit_card">Cartão</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : null}

      {checkoutTab === 'pix' && (pixProvider === 'pagarme' || pixProvider === 'mercadopago') ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe o CPF do pagador e clique para gerar o QR Code e o código Pix copia e cola.
          </Typography>
          <TextField
            label="CPF do pagador"
            fullWidth
            value={pixCpf}
            onChange={(e) => setPixCpf(applyCpfMask(e.target.value))}
            placeholder="000.000.000-00"
            sx={{ mb: 2 }}
            disabled={pagarmePixBusy}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={pagarmePixBusy || pixCpfDigits.length < 11 || !onRequestPagarmePix}
            onClick={() => onRequestPagarmePix?.(pixCpfDigits)}
          >
            {pagarmePixBusy ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Gerando Pix…
              </>
            ) : (
              'Gerar Pix'
            )}
          </Button>
        </Box>
      ) : null}

      {checkoutTab === 'credit_card' && cardProvider === 'mercadopago' ? (
        <PaymentBrick
          amount={amount}
          preferenceId={preferenceId}
          payerEmail={payerEmail}
          onCardTokenReceived={onCardTokenReceived}
          onError={onError}
          allowInstallments={allowInstallments}
          maxInstallments={maxInstallments}
          brickMode="card_only"
        />
      ) : null}

      {checkoutTab === 'credit_card' && cardProvider === 'pagarme' ? (
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={28} />
              <Typography variant="body2">Carregando formulário de cartão…</Typography>
            </Box>
          }
        >
          <PagarMePaymentBrick
            amount={amount}
            onCardTokenReceived={onCardTokenReceived}
            onError={onError}
            allowInstallments={allowInstallments}
            customerEmail={payerEmail || ''}
            customerName={customerName}
            customerCPF={customerCPF}
          />
        </Suspense>
      ) : null}
    </Box>
  );
};

export default CheckoutPaymentRouter;
